import * as QuoteDetailActions from './quote-detail.actions';

import { Action, createReducer, on } from '@ngrx/store';
import {
	ContractRates,
	Division,
	Job,
	JobOverTimeBasis,
	JobRegularTimeBasis,
	PagedResultsOfQuoteLogSummary
} from 'src/shared/api';
import { EntityState, createEntityAdapter } from '@ngrx/entity';

import { offeredServicesToAllowTbdJobDates } from 'src/shared/utilities/utility-functions';
import { quoteFormToQuote } from '../quote-detail.utility';

export const quoteDetailFeatureKey = 'quote-detail';

export interface QuoteDetailsState extends EntityState<Job> {
	selectedQuoteId?: number;
	error?: any;
	isLoading?: boolean;
	isSaving?: boolean;
	shouldStayOnPage?: boolean;
	railroadId?: number;
	regularTimeBasis?: keyof typeof JobRegularTimeBasis;
	overtimeBasis?: keyof typeof JobOverTimeBasis;
	customerId?: number;
	division?: Division;
	contractedRates?: ContractRates[];
	isQuoteLogLoading?: boolean;
	isQuoteLogInitialized?: boolean;
	errorApprovingQuote?: string;
	pagedQuoteLogResults?: PagedResultsOfQuoteLogSummary;
	isApproving?: boolean;
	isPrintingQuoteReport?: boolean;
	allowTbdJobDates?: boolean;
}

export const quoteDetailAdapter = createEntityAdapter<Job>();

export interface QuoteDetailPartialState {
	readonly [quoteDetailFeatureKey]: QuoteDetailsState;
}

export const initialState: QuoteDetailsState = quoteDetailAdapter.getInitialState(
	{
		isLoading: true,
		isSaving: false,
		pagedQuoteLogResults: { results: [], resultCount: 0 },
		allowTbdJobDates: false,
	}
);

const quoteReducer = createReducer(
	initialState,
	on(
		QuoteDetailActions.offeredServicesUpdated,
		(state, { offeredServices }) => ({
			...state,
			allowTbdJobDates: offeredServicesToAllowTbdJobDates(
				offeredServices
			),
		})
	),
	on(QuoteDetailActions.servicesAdded, (state, { serviceTypes }) => ({
		...state,
		contractedRates: [
			...(state.contractedRates || []),
			...serviceTypes.map((serviceType) => ({ serviceType })),
		],
	})),
	on(QuoteDetailActions.servicesRemoved, (state, { serviceTypeIds }) => ({
		...state,
		contractedRates: (
			state.contractedRates ||
			state.entities[state.selectedQuoteId].contractedRates
		).filter((r) => !serviceTypeIds.includes(r.serviceType.id)),
	})),
	on(QuoteDetailActions.quoteLoadedFromStore, (state, { quote }) => ({
		...state,
		railroadId: quote.railroad ? quote.railroad.id : undefined,
		customerId: quote.customer ? quote.customer.id : undefined,
		division: quote.division,
		overtimeBasis: quote.overTimeBasis,
		regularTimeBasis: quote.regularTimeBasis,
		contractedRates: quote.contractedRates,
		allowTbdJobDates: offeredServicesToAllowTbdJobDates(
			quote.offeredServices
		),
		isLoading: false,
	})),
	on(
		QuoteDetailActions.customerRatesApplied,
		(state, { contractedRates }) => ({
			...state,
			contractedRates: [contractedRates],
		})
	),
	on(
		QuoteDetailActions.railroadRatesApplied,
		(state, { contractedRates }) => ({
			...state,
			contractedRates: contractedRates,
		})
	),
	on(QuoteDetailActions.saveRequested, (state, { shouldStayOnPage }) => ({
		...state,
		isSaving: true,
		shouldStayOnPage,
	})),
	on(QuoteDetailActions.approveQuoteConfirmed, (state) => ({
		...state,
		isApproving: true,
	})),
	on(QuoteDetailActions.printQuoteReportConfirmed, (state) => ({
		...state,
		isPrintingQuoteReport: true,
	})),
	on(QuoteDetailActions.quoteReportPrinted, (state) => ({
		...state,
		isPrintingQuoteReport: false,
	})),
	on(QuoteDetailActions.quoteTurnedIntoJob, (state) => ({
		...state,
		isApproving: false,
	})),
	on(QuoteDetailActions.errorApprovingQuote, (state) => ({
		...state,
		isApproving: false,
	})),
	on(QuoteDetailActions.newQuoteSaved, (state) =>
		quoteDetailAdapter.removeOne(0, state)
	),
	on(QuoteDetailActions.newQuoteSaved, (state, { quote }) =>
		quoteDetailAdapter.addOne(quote, { ...state, isSaving: false })
	),
	on(QuoteDetailActions.quoteSaved, (state, { quote }) =>
		quoteDetailAdapter.updateOne(
			{
				id: quote.id,
				changes: {
					...quote,
				},
			},
			{ ...state, isSaving: false }
		)
	),
	on(QuoteDetailActions.quoteSelected, (state, { quoteId }) => ({
		...state,
		isQuoteLogLoading: true,
		isLoading: true,
		selectedQuoteId: quoteId,
	})),
	on(QuoteDetailActions.quoteLoadedFromApi, (state, { quote }) =>
		quoteDetailAdapter.upsertOne(quote, {
			...state,
			railroadId: quote.railroad ? quote.railroad.id : undefined,
			customerId: quote.customer ? quote.customer.id : undefined,
			overtimeBasis: quote.overTimeBasis,
			regularTimeBasis: quote.regularTimeBasis,
			contractedRates: quote.contractedRates,
			division: quote.division,
			isLoading: false,
			allowTbdJobDates: offeredServicesToAllowTbdJobDates(
				quote.offeredServices
			),
		})
	),
	on(QuoteDetailActions.documentsLoaded, (state, { documents }) =>
		quoteDetailAdapter.updateOne(
			{
				id: state.selectedQuoteId,
				changes: {
					documents,
				},
			},
			state
		)
	),
	on(QuoteDetailActions.userConfirmedPrevailingWage, (state, { quoteForm }) =>
		quoteDetailAdapter.updateOne(
			{
				id: state.selectedQuoteId,
				changes: {
					...quoteFormToQuote(quoteForm),
				},
			},
			state
		)
	),
	on(
		QuoteDetailActions.prevailingWagesConfirmed,
		(state, { prevailingWageInfo }) =>
			quoteDetailAdapter.updateOne(
				{
					id: state.selectedQuoteId,
					changes: {
						prevailingWageInfo,
					},
				},
				state
			)
	),
	on(QuoteDetailActions.errorSavingQuote, (state, { error }) => ({
		...state,
		error,
		isSaving: false,
	})),
	on(QuoteDetailActions.updatedRailroadLoaded, (state, { railroad }) => ({
		...state,
		overtimeBasis: railroad.overtimeBasis,
		regularTimeBasis: railroad.regularTimeBasis,
	})),
	on(QuoteDetailActions.railroadSelected, (state, { railroadId }) => ({
		...state,
		railroadId,
		division: undefined,
	})),
	on(QuoteDetailActions.customerSelected, (state, { customerId }) => ({
		...state,
		customerId,
	})),
	on(QuoteDetailActions.divisionSelected, (state, { division }) => ({
		...state,
		division,
	})),
	on(QuoteDetailActions.newQuoteStarted, (state) =>
		quoteDetailAdapter.addOne(
			{ id: 0 },
			{ ...state, isLoading: false, isQuoteLogLoading: false }
		)
	),
	on(QuoteDetailActions.quoteLogAdded, (state) => ({
		...state,
		isQuoteLogLoading: false,
		isQuoteLogInitialized: true,
	})),
	on(QuoteDetailActions.filtersUpdated, (state) => ({
		...state,
		isQuoteLogLoading: true,
	})),
	on(
		QuoteDetailActions.pagedQuoteLogsLoaded,
		(state, { pagedQuoteLogResults }) => ({
			...state,
			pagedQuoteLogResults,
			isQuoteLogLoading: false,
			isQuoteLogInitialized: true,
		})
	),
	on(QuoteDetailActions.quoteDetailDestroyed, () => quoteDetailAdapter.getInitialState(
		{
			isLoading: true,
			isSaving: false,
			pagedQuoteLogResults: { results: [], resultCount: 0 },
			allowTbdJobDates: false,
		}
	))
);

export function reducer(state: QuoteDetailsState | undefined, action: Action) {
	return quoteReducer(state, action);
}
